import React, { useState, useEffect, useCallback } from 'react';
import { Alert, AlertTitle, Box, Chip, Stack, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { REPS_COLORS } from '../utils/constants';

const DISMISSED_KEY = 'hugo_dismissed_warnings';

/**
 * Get dismissed warning keys from localStorage.
 * Each key is "quadrant:score:threshold" to uniquely identify a warning state.
 */
function getDismissedKeys() {
  try {
    const stored = localStorage.getItem(DISMISSED_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveDismissedKeys(keys) {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(keys));
  } catch {
    // localStorage not available
  }
}

function warningKey(w) {
  // Use quadrant + rounded score to avoid dismissing then re-showing on tiny score changes
  return `${w.quadrant}:${Math.round((w.score || 0) * 10)}:${w.threshold}`;
}

function WarningBanner({ warnings = [] }) {
  const [dismissedKeys, setDismissedKeys] = useState(() => getDismissedKeys());

  // Sync dismissed keys to localStorage
  useEffect(() => {
    saveDismissedKeys(dismissedKeys);
  }, [dismissedKeys]);

  const handleDismiss = useCallback((key) => {
    setDismissedKeys((prev) => [...prev, key]);
  }, []);

  // Filter out dismissed warnings
  const visibleWarnings = warnings.filter(
    (w) => !dismissedKeys.includes(warningKey(w))
  );

  if (visibleWarnings.length === 0) return null;

  const quadrantLabel = (q) =>
    q ? q.charAt(0).toUpperCase() + q.slice(1) : '';

  return (
    <Box sx={{ mb: 2 }}>
      {visibleWarnings.map((w) => {
        const key = warningKey(w);
        const color = REPS_COLORS[w.quadrant] || '#c62828';

        return (
          <Alert
            key={key}
            severity="error"
            icon={<WarningAmberIcon />}
            onClose={() => handleDismiss(key)}
            sx={{
              mb: 1,
              borderLeft: `4px solid ${color}`,
              '& .MuiAlert-icon': { color },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600 }}>
              Threshold Exceeded: {quadrantLabel(w.quadrant)}
            </AlertTitle>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2">
                {w.message}
              </Typography>
              <Chip
                label={`Score: ${(w.score || 0).toFixed(1)}`}
                size="small"
                sx={{
                  bgcolor: color,
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
              <Chip
                label={`Threshold: ${w.threshold}`}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: color,
                  color,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
            </Stack>
          </Alert>
        );
      })}
    </Box>
  );
}

export default WarningBanner;
